import { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Edges } from "@react-three/drei";
import * as THREE from "three";
import { motion } from "framer-motion";

const INK = "#0F172A";
const BLUE = "#0033A0";

const Box = ({ position, args, fillOpacity = 0.5, edgeColor = INK }) => (
  <mesh position={position}>
    <boxGeometry args={args} />
    <meshBasicMaterial color="#FFFFFF" transparent opacity={fillOpacity} polygonOffset polygonOffsetFactor={1} />
    <Edges color={edgeColor} threshold={20} />
  </mesh>
);

const Cyl = ({ position, args, rotation = [0, 0, 0], fillOpacity = 0.5 }) => (
  <mesh position={position} rotation={rotation}>
    <cylinderGeometry args={args} />
    <meshBasicMaterial color="#FFFFFF" transparent opacity={fillOpacity} polygonOffset polygonOffsetFactor={1} />
    <Edges color={INK} threshold={20} />
  </mesh>
);

// Lead sheets dropping in from above — flush, floor-to-ceiling, whole walls
const PANELS = [
  { pos: [-3.95, 1.6, -2.25], args: [0.05, 3.2, 1.5] },
  { pos: [-3.95, 1.6, -0.75], args: [0.05, 3.2, 1.5] },
  { pos: [-3.95, 1.6, 0.75], args: [0.05, 3.2, 1.5] },
  { pos: [-3.95, 1.6, 2.25], args: [0.05, 3.2, 1.5] },
  { pos: [-2.65, 1.6, -2.95], args: [1.35, 3.2, 0.05] },
  { pos: [-1.3, 1.6, -2.95], args: [1.35, 3.2, 0.05] },
  { pos: [0.05, 1.6, -2.95], args: [1.35, 3.2, 0.05] },
  { pos: [1.4, 1.6, -2.95], args: [1.35, 3.2, 0.05] },
  { pos: [3.61, 1.6, -2.95], args: [0.72, 3.2, 0.05] },
];

const LeadPanels = () => {
  const group = useRef();

  useFrame((state) => {
    if (!group.current) return;
    const CYCLE = 7;
    const EXIT_AT = 6.15;
    const elapsed = state.clock.elapsedTime;
    const cycle = Math.floor(elapsed / CYCLE);
    const t = elapsed % CYCLE;
    group.current.children.forEach((panel, i) => {
      const delay = 0.5 + i * 0.22;
      const p = Math.min(Math.max((t - delay) / 0.9, 0), 1);
      const e = 1 - Math.pow(1 - p, 3);
      let y = panel.userData.y + (1 - e) * 2.7;
      let vis = e;
      if (t > EXIT_AT) {
        const q = (t - EXIT_AT) / (CYCLE - EXIT_AT);
        y = panel.userData.y + q * 2.7;
        vis = 1 - q * 0.6;
      }
      panel.position.y = y;
      panel.children.forEach((c) => {
        c.material.transparent = true;
        c.material.opacity = vis;
      });
      panel.material.opacity = 0.16 * vis;
    });
  });

  return (
    <group ref={group}>
      {PANELS.map((p, i) => (
        <mesh key={i} position={[p.pos[0], p.pos[1] + 2.7, p.pos[2]]} userData={{ y: p.pos[1] }}>
          <boxGeometry args={p.args} />
          <meshBasicMaterial color={BLUE} transparent opacity={0} depthWrite={false} />
          <Edges color={BLUE} threshold={20} />
        </mesh>
      ))}
    </group>
  );
};

// Patient — tapered torso, distinct legs, arms at the sides, feet; torso rises gently (breathing)
const Patient = () => {
  const torso = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const s = 1 + Math.sin(t * 1.3) * 0.05;
    if (torso.current) torso.current.scale.set(s, 1, s);
  });
  const mat = <meshBasicMaterial color="#FFFFFF" transparent opacity={0.35} depthWrite={false} />;
  const lie = [0, 0, Math.PI / 2];
  return (
    <>
      {/* head + neck */}
      <mesh position={[-2.33, 1.06, -0.5]}>
        <sphereGeometry args={[0.105, 18, 14]} />
        {mat}
        <Edges color={INK} threshold={20} />
      </mesh>
      <mesh position={[-2.17, 1.02, -0.5]} rotation={lie}>
        <cylinderGeometry args={[0.045, 0.05, 0.1, 10]} />
        {mat}
        <Edges color={INK} threshold={20} />
      </mesh>
      {/* torso — wider at the shoulders, tapering to the hips */}
      <mesh ref={torso} position={[-1.75, 1.03, -0.5]} rotation={lie}>
        <cylinderGeometry args={[0.185, 0.135, 0.6, 12]} />
        {mat}
        <Edges color={INK} threshold={20} />
      </mesh>
      {/* arms resting alongside the torso */}
      {[-0.31, -0.69].map((z) => (
        <mesh key={`arm-${z}`} position={[-1.78, 0.99, z]} rotation={lie}>
          <capsuleGeometry args={[0.045, 0.5, 4, 8]} />
          {mat}
          <Edges color={INK} threshold={20} />
        </mesh>
      ))}
      {/* two legs */}
      {[-0.575, -0.425].map((z) => (
        <mesh key={`leg-${z}`} position={[-0.88, 1.0, z]} rotation={lie}>
          <capsuleGeometry args={[0.068, 0.85, 5, 10]} />
          {mat}
          <Edges color={INK} threshold={20} />
        </mesh>
      ))}
      {/* feet */}
      {[-0.575, -0.425].map((z) => (
        <mesh key={`foot-${z}`} position={[-0.36, 1.0, z]}>
          <boxGeometry args={[0.1, 0.09, 0.085]} />
          {mat}
          <Edges color={INK} threshold={20} />
        </mesh>
      ))}
    </>
  );
};

// Scatter pulse — during the installed phase of each cycle, rays grow from the
// patient and stop at the freshly shielded walls with a contact flash
const SCATTER_TARGETS = [
  [-3.88, 1.3, -2.0], [-3.88, 0.9, -1.0], [-3.88, 1.5, 0.2], [-3.88, 0.7, 1.4],
  [-2.4, 1.4, -2.88], [-1.2, 0.8, -2.88], [0.1, 1.5, -2.88], [-0.4, 0.02, 1.6],
];
const SCATTER_SRC = new THREE.Vector3(-1.3, 1.05, -0.5);

const ScatterPulse = () => {
  const rays = useRef([]);
  const flashes = useRef([]);
  const ring = useRef();
  const data = useRef(null);
  if (!data.current) {
    data.current = SCATTER_TARGETS.map((tp) => {
      const target = new THREE.Vector3(...tp);
      const dir = target.clone().sub(SCATTER_SRC);
      const len = dir.length();
      dir.normalize();
      const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
      return { dir, len, quat, target };
    });
  }

  useFrame((state) => {
    const t = state.clock.elapsedTime % 7;
    data.current.forEach((d, i) => {
      const ray = rays.current[i];
      const flash = flashes.current[i];
      if (!ray || !flash) return;
      const local = (t - (3.2 + i * 0.08)) / 1.1;
      const p = Math.min(Math.max(local, 0), 1);
      const grow = 1 - Math.pow(1 - p, 2);
      ray.scale.y = Math.max(grow * d.len, 0.0001);
      ray.position.copy(SCATTER_SRC).addScaledVector(d.dir, (grow * d.len) / 2);
      ray.quaternion.copy(d.quat);
      let opacity = p <= 0 ? 0 : p < 0.1 ? p / 0.1 : 1;
      if (local > 0.9) opacity *= Math.max(1 - (local - 0.9) / 0.55, 0);
      ray.material.opacity = opacity * 0.75;
      flash.position.copy(d.target);
      const fo = p >= 0.95 ? Math.max(0, 1 - (local - 0.95) / 0.6) : 0;
      flash.material.opacity = fo * 0.9;
      const fs = 1 + (local - 0.95) * 1.5;
      flash.scale.setScalar(Math.max(fs, 0.5));
    });
    if (ring.current) {
      const r = (t - 3.2) / 1.6;
      const on = r > 0 && r < 1;
      ring.current.visible = on;
      if (on) {
        ring.current.scale.setScalar(0.15 + r * 3.1);
        ring.current.material.opacity = (1 - r) * 0.4;
      }
    }
  });

  return (
    <>
      {data.current.map((d, i) => (
        <mesh key={`ray-${i}`} ref={(el) => (rays.current[i] = el)}>
          <cylinderGeometry args={[0.009, 0.009, 1, 6]} />
          <meshBasicMaterial color="#2563EB" transparent opacity={0} depthWrite={false} />
        </mesh>
      ))}
      {data.current.map((d, i) => (
        <mesh key={`flash-${i}`} ref={(el) => (flashes.current[i] = el)}>
          <sphereGeometry args={[0.045, 10, 8]} />
          <meshBasicMaterial color={BLUE} transparent opacity={0} depthWrite={false} />
        </mesh>
      ))}
      <mesh ref={ring} position={[-1.3, 0.02, -0.5]} rotation={[-Math.PI / 2, 0, 0]} visible={false}>
        <ringGeometry args={[0.92, 1, 48]} />
        <meshBasicMaterial color="#2563EB" transparent opacity={0} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
    </>
  );
};

const Rig = ({ children }) => {
  const g = useRef();
  const t0 = useRef(0);
  useFrame((state, delta) => {
    t0.current += delta;
    const sway = Math.sin(t0.current * 0.22) * 0.38;
    const target = sway + state.pointer.x * 0.18;
    g.current.rotation.y = THREE.MathUtils.lerp(g.current.rotation.y, target, 0.045);
    g.current.rotation.x = THREE.MathUtils.lerp(g.current.rotation.x, state.pointer.y * 0.05, 0.045);
  });
  return <group ref={g} position={[0.2, -1.1, 0]}>{children}</group>;
};

const Room = () => (
  <>
    {/* floor grid */}
    <gridHelper
      args={[8, 8, BLUE, "#D9E3F5"]}
      onUpdate={(g) => {
        g.material.transparent = true;
        g.material.opacity = 0.55;
      }}
    />
    {/* back + left walls */}
    <Box position={[0, 1.6, -3]} args={[8, 3.2, 0.06]} fillOpacity={0.16} />
    <Box position={[-4, 1.6, 0]} args={[0.06, 3.2, 6]} fillOpacity={0.16} />
    {/* wall seams */}
    {[-2, 0, 2].map((x) => (
      <mesh key={`bs-${x}`} position={[x, 1.6, -2.98]}>
        <boxGeometry args={[0.015, 3.2, 0.02]} />
        <meshBasicMaterial color={BLUE} transparent opacity={0.3} />
      </mesh>
    ))}
    {/* door on the back wall */}
    <Box position={[2.7, 1.05, -2.94]} args={[1.05, 2.1, 0.05]} fillOpacity={0.3} />

    {/* lead shielding panels, installed from above */}
    <LeadPanels />

    {/* chest bucky, proud of the shielded left wall */}
    <Box position={[-3.86, 1.15, -2.4]} args={[0.1, 1.9, 0.85]} fillOpacity={0.4} />
    <mesh position={[-3.8, 1.35, -2.4]}>
      <boxGeometry args={[0.02, 0.9, 0.6]} />
      <meshBasicMaterial color={BLUE} transparent opacity={0.2} />
    </mesh>

    {/* patient table — top, with cabinet pedestal and base nearly the size of the top */}
    <Box position={[-1.3, 0.86, -0.5]} args={[2.5, 0.09, 0.95]} fillOpacity={0.6} />
    <Box position={[-1.3, 0.44, -0.5]} args={[2.2, 0.78, 0.8]} fillOpacity={0.45} />
    <Box position={[-1.3, 0.065, -0.5]} args={[2.3, 0.13, 0.85]} fillOpacity={0.5} />

    <Patient />

    <ScatterPulse />

    {/* ceiling tracks */}
    <Box position={[-0.8, 3.02, -1.15]} args={[4.6, 0.05, 0.12]} fillOpacity={0.35} />
    <Box position={[-0.8, 3.02, -0.35]} args={[4.6, 0.05, 0.12]} fillOpacity={0.35} />
    {/* carriage + telescoping column */}
    <Box position={[-1.3, 2.94, -0.75]} args={[0.4, 0.14, 1.05]} fillOpacity={0.55} />
    <Cyl position={[-1.3, 2.5, -0.75]} args={[0.075, 0.075, 0.8, 12]} fillOpacity={0.6} />
    <Cyl position={[-1.3, 2.12, -0.75]} args={[0.05, 0.05, 0.35, 12]} fillOpacity={0.6} />

    {/* tube housing + collimator */}
    <Box position={[-1.3, 1.82, -0.75]} args={[0.72, 0.32, 0.46]} fillOpacity={0.7} />
    <Cyl position={[-1.3, 1.56, -0.75]} args={[0.1, 0.17, 0.22, 4]} rotation={[0, Math.PI / 4, 0]} fillOpacity={0.65} />

    {/* primary beam — translucent cone to the patient */}
    <mesh position={[-1.3, 1.2, -0.68]} rotation={[0.06, Math.PI / 4, 0]}>
      <coneGeometry args={[0.42, 0.62, 4, 1, true]} />
      <meshBasicMaterial color={BLUE} transparent opacity={0.08} side={THREE.DoubleSide} depthWrite={false} />
    </mesh>
    {/* collimator light field on the tabletop */}
    <mesh position={[-1.3, 0.912, -0.62]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[0.66, 0.5]} />
      <meshBasicMaterial color={BLUE} transparent opacity={0.16} depthWrite={false} />
    </mesh>
    <mesh position={[-1.3, 0.915, -0.62]} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[0.05, 0.065, 24]} />
      <meshBasicMaterial color={BLUE} transparent opacity={0.5} depthWrite={false} />
    </mesh>
  </>
);

const WireframeRoom = () => (
  <motion.div
    data-testid="hero-wireframe-room"
    className="relative h-full w-full"
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
  >
    <Canvas
      orthographic
      dpr={[1, 2]}
      camera={{ position: [9.5, 7.5, 9.5], zoom: 66 }}
      gl={{ antialias: true, alpha: true }}
    >
      <Rig>
        <Room />
      </Rig>
    </Canvas>
  </motion.div>
);

export default WireframeRoom;
